<script setup>
  
  import { useStageStore } from '../stores/stage';
  import { useI18n } from 'vue-i18n';
  import { computed, ref } from 'vue'

  const { t, te } = useI18n()
  const formStorage = useStageStore();
  const currentLang = formStorage.language;

  const resultsPathTypeArray = computed(() => {
    return formStorage.pointsPerPathType;
  })

  const resultsArray = computed(() => {
    const map = new Map();

    // Merge formStorage data into single result object
    formStorage.stagesData?.data?.stages?.forEach(item => map.set(String(item.id), item))
    formStorage.answersData?.forEach(item => map.set(String(item.id), {...map.get(String(item.id)), ...item}));
    formStorage.pointsPerStage?.forEach(item => map.set(String(item.id), {...map.get(String(item.id)), ...item}));

    return Array.from(map.values());
  })

  const getPoorResults = (resultsArray) => {
    return resultsArray.filter(obj => (obj.points && true && obj.points[0] === 0 || (obj.answer && true && obj.answer === "blockedByRelations")))
  }

  const getResultTitle = (result) => {
    // Check if localized title exists in the result object
    if (!result || !currentLang in result) {
      return '';
    }

    return result[currentLang];
  }
  const getResultAnswer = (result) => {
    let resultValue = result;

    if (typeof result === 'string' && te(`table.${result}`)) {
      // Localized formatting for e.g. "clicked" answers
      resultValue = t(`table.${result}`)
    }
    
    return resultValue;
  }
  const getResultPoints = (result) => {
    if (!Array.isArray(result) || !result.length) {
      return undefined // Missing results are not calculated
    }
    
    return result.reduce((previousValue, currentValue) => previousValue + currentValue, 0)
  }
  const getTotalPoints = () => {
    if (!formStorage.pointsPerStage.length) {
      return '';
    }

    return formStorage.pointsPerStage.reduce((previousValue, currentValue) => {
        const pointsResult = getResultPoints(currentValue.points) ?? 0; // Missing results substituted with 0 in calculations 
        return previousValue + pointsResult
    }, 0)
  }

  const getReviewText = () => {
    return Number(getTotalPoints())/Number(formStorage.stagesData?.data?.surveyMaxPointsPossible)*100;
  }

  const printResults = () => {

    // Define event listener to clear temporary elements after printing
    window.onafterprint = function(){

      const bodyClasses = document.querySelector('body').classList
      if (bodyClasses && bodyClasses.contains('assessment-survey-hide-during-print')) {
        bodyClasses.remove('assessment-survey-hide-during-print')
        document.querySelector('.assessment-survey-temporary-print-container').remove()
      }
    }

    // Create temporary print container
    const temporaryPrintContainer = document.createElement("div")
    temporaryPrintContainer.classList.add("assessment-survey-temporary-print-container")

    // Clone and insert app container into print container
    temporaryPrintContainer.append(document.querySelector('.yja-assessment-survey').cloneNode(true))

    document.querySelector('html').prepend(temporaryPrintContainer)

    // Hide normal body class during print
    document.querySelector('body').classList.add('assessment-survey-hide-during-print')

    window.print()
  }
</script>

<template>
  <h2>{{ t('thank-you-message') }}</h2>

  <p>{{ t('total-points-per-topic')}}{{ getTotalPoints() }}/{{ formStorage.stagesData?.data?.surveyMaxPointsPossible }}</p>
  <p v-if="getReviewText() >= 75">{{ t('review-text-pos') }}</p>
  <p v-if="getReviewText() >= 50 && getReviewText() < 75">{{ t('review-text-med') }}</p>
  <p v-if="getReviewText() < 50">{{ t('review-text-neg') }}</p>

  <div>{{ t('survey-result-view-text-1')}}</div>

  <table class="summary-table">
    <tbody>
    <tr v-for="result in resultsPathTypeArray" :key="result.id" class="result-row">
      <td>{{ result.name }} </td>
      <td>{{ result.points }}/{{result.maxPoints}}</td>
    </tr>
    </tbody>
  </table>

  <div class="mt-2 completion-row">
    <p>{{ `${t('table.completion')} ${formStorage.calculateSurveyProgressInPercents() + '%'}` }}</p>
  </div>

  <div>
    <p>{{ t('survey-result-view-text-2')}}</p>
    <p>{{ t('survey-result-view-text-3')}}<a
        href="https://tilatjaterveys.fi/toimintamalli"
        target="_blank"
    >https://tilatjaterveys.fi/toimintamalli</a>)</p>
  </div>

  <h2 v-if="getPoorResults(resultsArray).length > 0">{{t('improving-topics')}}</h2>

  <table class="results-table" v-if="getPoorResults(resultsArray).length > 0">
    <thead>
      <tr>
        <th scope="col">{{ t('table.plain-question') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="result in getPoorResults(resultsArray)" :key="result.id" class="result-row">
        <td>{{ getResultTitle(result.node.components[0].label) }} </td>
      </tr>
    </tbody>
  </table>

  <hr>
  <div class="results-actions assessment-survey-hide-during-print">
    <button @click="printResults()" class="btn btn-secondary print" value="print" type="button">
      <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-printer" viewBox="0 0 16 16">
        <path d="M2.5 8a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
        <path d="M5 1a2 2 0 0 0-2 2v2H2a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1v1a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1V3a2 2 0 0 0-2-2H5zM4 3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2H4V3zm1 5a2 2 0 0 0-2 2v1H2a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v-1a2 2 0 0 0-2-2H5zm7 2v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1z"/>
      </svg>
      <span>{{ t('button.print') }}</span>
    </button>
  </div>
</template>